import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LuTrash2, LuPlus, LuPencil } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { PhotoInput } from "../shared/PhotoInput";

// Test procedures written for one site, read by whoever works it next.
//
// Authored here by the office only — a field tech sees these read-only, from
// a button on the inspection they are running. Steps are a numbered list with
// an optional photo each, in the same add/remove row shape the quote item
// table uses; there is no drag-and-drop anywhere in this app, so order is the
// row position and gets written as an explicit `order` on save.
export function ProceduresTab({ siteId }) {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, else a draft
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    axios
      .get(`${API}/site-procedures/?site=${siteId}`)
      .then((r) => setProcedures(r.data || []))
      .catch(() => setError("Could not load this site's procedures."))
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () =>
    setEditing({ id: null, title: "", intro: "", steps: [emptyStep()] });

  const startEdit = (p) =>
    setEditing({
      id: p.id,
      title: p.title,
      intro: p.intro || "",
      steps: (p.steps || []).map((s) => ({
        id: s.id,
        text: s.text || "",
        photo_url: s.photo_url || null,
        photo: null,
      })),
    });

  const setStep = (idx, patch) =>
    setEditing((e) => ({
      ...e,
      steps: e.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  const save = async () => {
    if (!editing.title.trim()) return setError("Give the procedure a title.");
    setSaving(true);
    setError("");
    try {
      let procedureId = editing.id;
      if (procedureId) {
        await axios.patch(`${API}/site-procedures/${procedureId}/`, {
          title: editing.title,
          intro: editing.intro,
        });
      } else {
        const res = await axios.post(`${API}/site-procedures/`, {
          site: siteId,
          title: editing.title,
          intro: editing.intro,
        });
        procedureId = res.data.id;
      }

      // Steps are written one row at a time — this codebase writes children
      // through their own endpoint rather than nesting them in the parent.
      for (let i = 0; i < editing.steps.length; i += 1) {
        const step = editing.steps[i];
        const body = new FormData();
        body.append("order", i);
        body.append("text", step.text || "");
        if (step.photo) body.append("photo", step.photo);
        if (step.id) {
          await axios.patch(`${API}/procedure-steps/${step.id}/`, body);
        } else {
          body.append("procedure", procedureId);
          await axios.post(`${API}/procedure-steps/`, body);
        }
      }

      for (const id of editing.removedStepIds || []) {
        await axios.delete(`${API}/procedure-steps/${id}/`);
      }

      setEditing(null);
      load();
    } catch (err) {
      setError(
        err.response?.data?.detail || "Could not save that procedure."
      );
    }
    setSaving(false);
  };

  const removeProcedure = async (p) => {
    if (!window.confirm(`Delete "${p.title}" and all its steps?`)) return;
    try {
      await axios.delete(`${API}/site-procedures/${p.id}/`);
      load();
    } catch {
      setError("Could not delete that procedure.");
    }
  };

  if (loading) return <div style={styles.loading}>Loading…</div>;

  // ── The editor ────────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div style={styles.card}>
        <div style={{ padding: "20px" }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Title *</label>
            <input
              style={inputStyle}
              autoFocus
              value={editing.title}
              onChange={(e) =>
                setEditing((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Emergency Light Test Procedure"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Introduction</label>
            <textarea
              style={{ ...inputStyle, height: "70px", resize: "vertical" }}
              value={editing.intro}
              onChange={(e) =>
                setEditing((p) => ({ ...p, intro: e.target.value }))
              }
              placeholder="What this covers, and anything to have on the van before starting."
            />
          </div>

          <div style={{ ...labelStyle, marginTop: "18px" }}>Steps</div>
          {editing.steps.map((step, idx) => (
            <div
              key={step.id ?? `new-${idx}`}
              style={{
                display: "flex",
                gap: "12px",
                padding: "12px 0",
                borderTop: `1px solid ${colors.border}`,
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  backgroundColor: colors.lightGrey,
                  color: colors.slate,
                  fontSize: "13px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {idx + 1}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <textarea
                  style={{ ...inputStyle, height: "62px", resize: "vertical" }}
                  value={step.text}
                  onChange={(e) => setStep(idx, { text: e.target.value })}
                  placeholder="What the tech does at this step."
                />
                <div style={{ marginTop: "8px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  {(step.photo || step.photo_url) && (
                    <img
                      src={
                        step.photo
                          ? URL.createObjectURL(step.photo)
                          : step.photo_url
                      }
                      alt=""
                      style={{
                        width: "90px",
                        height: "68px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        border: `1px solid ${colors.border}`,
                      }}
                    />
                  )}
                  <PhotoInput
                    compact
                    takeLabel={step.photo || step.photo_url ? "Retake" : "Take photo"}
                    libraryLabel={step.photo || step.photo_url ? "Replace" : "Library"}
                    onFiles={(files) => files[0] && setStep(idx, { photo: files[0] })}
                  />
                </div>
              </div>

              <button
                onClick={() =>
                  setEditing((e) => ({
                    ...e,
                    steps: e.steps.filter((_, i) => i !== idx),
                    // Rows already saved must be deleted server-side too.
                    removedStepIds: step.id
                      ? [...(e.removedStepIds || []), step.id]
                      : e.removedStepIds,
                  }))
                }
                title="Remove step"
                style={{
                  flexShrink: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: colors.slate,
                  padding: "4px",
                  alignSelf: "flex-start",
                }}
              >
                <LuTrash2 size={15} />
              </button>
            </div>
          ))}

          <button
            onClick={() =>
              setEditing((e) => ({ ...e, steps: [...e.steps, emptyStep()] }))
            }
            style={{
              ...styles.addButton,
              backgroundColor: colors.teal,
              marginTop: "14px",
            }}
          >
            <LuPlus size={14} /> Add Step
          </button>

          {error && (
            <div style={{ color: colors.red, fontSize: "13px", marginTop: "12px" }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
            <button
              onClick={save}
              disabled={saving}
              style={{ ...styles.addButton, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? "Saving…" : "Save Procedure"}
            </button>
            <button
              onClick={() => {
                setEditing(null);
                setError("");
              }}
              style={{
                background: "none",
                border: `1px solid ${colors.border}`,
                borderRadius: "8px",
                padding: "10px 20px",
                cursor: "pointer",
                fontSize: "14px",
                color: colors.text,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── The list ──────────────────────────────────────────────────────────────
  return (
    <div>
      {error && (
        <div style={{ color: colors.red, fontSize: "13px", marginBottom: "12px" }}>
          {error}
        </div>
      )}

      <div style={styles.card}>
        {procedures.length === 0 ? (
          <div style={{ ...styles.loading, lineHeight: "1.6" }}>
            No procedures written for this site yet.
            <br />
            Techs see these read-only from the inspection they're running.
          </div>
        ) : (
          procedures.map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "14px 20px",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: "600", color: colors.text }}>
                  {p.title}
                </div>
                <div style={{ fontSize: "13px", color: colors.slate }}>
                  {(p.steps || []).length} step
                  {(p.steps || []).length === 1 ? "" : "s"}
                </div>
              </div>
              <div style={{ display: "flex", gap: "4px" }}>
                <button
                  onClick={() => startEdit(p)}
                  title="Edit"
                  style={iconBtn}
                >
                  <LuPencil size={15} />
                </button>
                <button
                  onClick={() => removeProcedure(p)}
                  title="Delete"
                  style={iconBtn}
                >
                  <LuTrash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <button onClick={startNew} style={{ ...styles.addButton, marginTop: "16px" }}>
        <LuPlus size={14} /> Add Procedure
      </button>
    </div>
  );
}

function emptyStep() {
  return { id: null, text: "", photo: null, photo_url: null };
}

const iconBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: colors.slate,
  padding: "4px",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
};
